import { Box, Typography } from "@mui/material";
import { Edit, Delete } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { memo, useEffect, useMemo, useState } from "react";

import JahrgangCard from "./JahrgangCard";
import { useAppContext } from "../../../2_ksbHub/context/appContext";
import { useSondertagContext } from "../context/sondertagContext";
import KsbActionButton from "../../../2_ksbHub/components/KsbActionButton";
import useSondertagStats from "../hooks/useSondertagStats";

function SondertagCard({
  day,
  jahrgaenge,
  viewMode,
  onEditJob,
  onDeleteJob,
  onEditSondertag,
  onDeleteSondertag,
}) {
  const theme = useTheme();

  const { currentUser, isTeacherView, isATeacher } = useAppContext();
  const { isASondertageEventAdmin, angebote } = useSondertagContext();

  // console.log("angebote: ", angebote);

  const [expanded, setExpanded] = useState(false);

  const { filledSlots, totalSlots, sondertagStats } = useSondertagStats(
    angebote,
    currentUser,
  );

  const stats = sondertagStats.find((s) => s.sondertagId === day.id);
  /**
   * =========================================================
   * ✅ DERIVED DATA (NEW MODEL)
   * =========================================================
   */

  const myReservationCount = useMemo(
    () =>
      jahrgaenge
        .flatMap((j) => j.angebote || [])
        .filter((a) => a.owners?.some((o) => o.id === currentUser?.id)).length,
    [jahrgaenge, currentUser?.id],
  );

  const freeCount = useMemo(
    () =>
      angebote.filter(
        (a) => !a.owners?.some((o) => o.abbreviation?.toLowerCase() !== "leer"),
      ).length,
    [angebote],
  );

  const leerReservationCount = useMemo(
    () =>
      jahrgaenge
        .flatMap((j) => j.angebote || [])
        .filter(
          (a) =>
            a.owners?.length === 1 &&
            a.owners[0]?.abbreviation?.toLowerCase() === "leer",
        ).length,
    [jahrgaenge],
  );

  /**
   * =========================================================
   * ✅ EXPANSION LOGIC
   * =========================================================
   */
  useEffect(() => {
    if (viewMode === "mine") {
      // setExpanded(myReservatonCount>0);
      setExpanded(false);
    } else if (viewMode === "leitung") {
      // setExpanded(freeCount > 0);
      setExpanded(false);
    } else if (viewMode === "leer") {
      // setExpanded(leerReservationCount > 0);
      setExpanded(false);
    } else if (viewMode === "all") {
      setExpanded(false);
    }
  }, [viewMode]);

  /**
   * =========================================================
   * ✅ RENDER
   * =========================================================
   */

  const shouldRender =
    viewMode === "all" ||
    (viewMode === "mine" && myReservationCount > 0) ||
    (viewMode === "leitung" && freeCount > 0) ||
    (viewMode === "leer" && leerReservationCount > 0);

  if (!shouldRender) return null;

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: theme.shape.borderRadius,
        backgroundColor:
          viewMode === "mine" && myReservationCount > 0
            ? `rgba(82,133,228,0.15)`
            : theme.palette.background.paper,
      }}
    >
      {/* ✅ HEADER */}
      <Box
        onClick={() => setExpanded((prev) => !prev)}
        sx={{
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: { xs: "space-between", md: "flex-start" },
          gap: 1,
          mb: 1,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            flex: { xs: 1, md: "unset" },
            minWidth: 0,
            fontWeight: 700,
            opacity: filledSlots === totalSlots ? 0.65 : 1,
          }}
        >
          {day.label} ({day.time})
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          {(isASondertageEventAdmin || isATeacher) &&
            stats &&
            (viewMode === "all" || viewMode === "leitung") && (
              <Typography sx={{ fontSize: "0.75rem", fontWeight: 600 }}>
                ({stats?.free} offen)
              </Typography>
            )}
          {(isASondertageEventAdmin || isATeacher) &&
            stats &&
            viewMode === "leer" && (
              <Typography sx={{ fontSize: "0.75rem", fontWeight: 600 }}>
                ({leerReservationCount} leer
                {leerReservationCount > 1 ? "e" : ""}))
              </Typography>
            )}
          {(isASondertageEventAdmin || isATeacher) &&
            stats &&
            viewMode === "mine" && (
              <Typography sx={{ fontSize: "0.75rem", fontWeight: 600 }}>
                ({myReservationCount} Reservation
                {myReservationCount === 1 ? "" : "en"})
              </Typography>
            )}
        </Box>
        <Typography sx={{ opacity: 0.6 }}>
          {expanded ? "▾" : "▸"}
        </Typography>{" "}
      </Box>

      {/* ✅ JAHRGAENGE */}
      {expanded && (
        <>
          <Box>
            {jahrgaenge.length === 0 ? (
              <Typography variant="body2" sx={{ opacity: 0.6 }}>
                Keine Einträge
              </Typography>
            ) : (
              jahrgaenge.map((jahrgang) => (
                <JahrgangCard
                  key={jahrgang.id}
                  sondertagId={day.id}
                  jahrgang={jahrgang}
                  allAngebote={angebote} // ✅ all angebote access
                  viewMode={viewMode}
                  onEdit={onEditJob}
                  onDelete={onDeleteJob}
                />
              ))
            )}
          </Box>

          {/* ✅ ADMIN ACTIONS */}
          {isASondertageEventAdmin && !isTeacherView && (
            <Box
              sx={{
                mt: 1.5,
                display: "flex",
                justifyContent: "flex-end",
                gap: 1,
              }}
            >
              <KsbActionButton
                onClick={() => onEditSondertag?.(day)}
                icon={<Edit />}
                label="Tag bearbeiten"
              />

              <KsbActionButton
                onClick={() => onDeleteSondertag(day.id)}
                icon={<Delete />}
                label="Löschen"
                confirm={{
                  title: "Delete item?",
                  message: "Are you sure you want to delete this item?",
                }}
                sx={{
                  bgcolor: theme.palette.error.main,
                  color: theme.palette.error.contrastText,
                  "&:hover": { bgcolor: theme.palette.error.dark },
                }}
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
}

export default memo(SondertagCard);
